import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TabBox from '../organisms/TabBox';
import List from '../organisms/List';
import VenueSpaceLayer from '../organisms/VenueSpaceLayer';
import AddItem from '../molecules/AddItem';
import VenueSpaceListed from '../molecules/VenueSpaceListed';
import Sorting from '../molecules/Sorting';
import Empty from '../atoms/Empty';
import { fields } from '../../data/fields';
import { display, spacesSortKeys } from '../../data/display';
import { byProperty } from '../../lib/utils';
import { convertValueToImperial, metricUnitKeys, Unit } from '../../lib/units';
import { isImperial, metricsName } from '../../features/metrics/metricsSlice';
import { useAppSelector } from '../../app/hooks';
import styles from '../../styles/pages/Venue.module.scss';
import {
  DragDropContext,
  Droppable,
  Draggable,
  resetServerContext,
} from 'react-beautiful-dnd';
import { LexoRank } from 'lexorank';

interface VenueSpacesProps {
  venueID: string;
  venuePlan: string;
  data: any;
  access?: boolean;
  apply: (id: string) => void;
  setLayer: (el: any) => void;
  setFullLayer: (el: any) => void;
  venueSpaceEditHandler?: (data: { id: string; lexorank: string }) => void;
}

const VenueSpaces = (props: VenueSpacesProps) => {
  const { t } = useTranslation(['common', 'venue', 'rating']);
  const { locale } = useRouter();
  const imperial = useAppSelector(isImperial);
  const unitsType = useAppSelector(metricsName);
  const [tab, setTab] = useState('exhibition_hall');
  const {
    venueID,
    venuePlan,
    data,
    setLayer,
    setFullLayer,
    access,
    apply,
    venueSpaceEditHandler,
  } = props;
  const [spacesSort, setSpacesSort] = useState({
    value: 'lexorank',
    order: 'asc',
  });
  const [isBrowser, setIsBrowser] = useState(false);
  const allFields = display.venue.platinum as any;
  const planFields = (() => {
    switch (true) {
      case venuePlan === 'BASIC':
        return display.venue.basic;
      case venuePlan === 'PLATINUM':
      case venuePlan === 'TRIAL_PLATINUM':
      case Boolean(access):
      default:
        return display.venue.platinum;
    }
  })();

  // Tabs
  const displayObj = allFields.spaces || {};
  const planDisplayObj = planFields.spaces || {};

  const currSpacesKeys = Object.keys(displayObj);
  const currSpacesTabs = currSpacesKeys.map((key) => ({
    id: key,
    label: t(`venue:spaces:${key}s`),
  }));

  // Sort options by tab
  const currSpacesSortKeys = spacesSortKeys[tab] || [];
  const currSpacesSort = currSpacesSortKeys.reduce(
    (acc: any, curr: any) => {
      const label = t(`venue:spaces:${curr}`).split('(')[0];
      return [
        ...acc,
        { label, value: `${curr}-desc`, order: 'desc', suffix: ' ↓' },
        { label, value: `${curr}-asc`, order: 'asc', suffix: ' ↑' },
      ];
    },
    [
      { value: 'name-asc', label: 'Name', order: 'asc', suffix: ' A-Z' },
      { value: 'name-desc', label: 'Name', order: 'desc', suffix: ' Z-A' },
    ]
  );

  // Properties by tab
  const propertiesByTab = displayObj[tab][0] || {};
  const planFieldsByTab = planDisplayObj[tab][0] || {};
  // Create/convert data object to fit ui requirements
  const getSpaceData = (obj: any) => {
    const {
      id,
      name,
      images,
      length,
      width,
      height,
      gates_width,
      gates_height,
      capacity_per_type,
      lexorank,
    } = obj;

    const type = obj.type || tab.toUpperCase();
    const len = length || fields.length.value;
    const wid = width || fields.width.value;
    const hei = height || fields.height.value;
    const l = imperial ? convertValueToImperial(len, fields.length.unit) : len;
    const w = imperial ? convertValueToImperial(wid, fields.width.unit) : wid;
    const h = imperial ? convertValueToImperial(hei, fields.height.unit) : hei;
    const gates_wid = gates_width || fields.gates_width.value;
    const gates_hei = gates_height || fields.gates_height.value;
    const gates_w = imperial
      ? convertValueToImperial(wid, fields.gates_width.unit)
      : gates_wid;
    const gates_h = imperial
      ? convertValueToImperial(hei, fields.gates_height.unit)
      : gates_hei;
    const pax_per_type = capacity_per_type || fields.capacity_per_type.value;
    const pax_type_1 =
      pax_per_type.split('/')[0].replace(/ /g, '') || fields.pax_type_1.value;
    const pax_type_2 =
      pax_per_type.split('/')[1].replace(/ /g, '') || fields.pax_type_2.value;
    const pax_type_3 =
      pax_per_type.split('/')[2].replace(/ /g, '') || fields.pax_type_3.value;
    const pax_type_4 =
      pax_per_type.split('/')[3].replace(/ /g, '') || fields.pax_type_4.value;
    const pax_type_5 =
      pax_per_type.split('/')[4].replace(/ /g, '') || fields.pax_type_5.value;
    const pax_type_6 =
      pax_per_type.split('/')[5].replace(/ /g, '') || fields.pax_type_6.value;
    const mergedObj = {
      ...obj,
      l_w_h: `${l.toLocaleString(locale, {
        maximumFractionDigits: 2,
      })} / ${w.toLocaleString(locale, {
        maximumFractionDigits: 2,
      })} / ${h.toLocaleString(locale, { maximumFractionDigits: 2 })}`,
      gates_w_h: `${gates_w.toLocaleString(locale, {
        maximumFractionDigits: 2,
      })} / ${gates_h.toLocaleString(locale, { maximumFractionDigits: 2 })}`,
      pax_type_1, // frontal
      pax_type_2, // frontal table
      pax_type_3, // long table
      pax_type_4, // horseshoe
      pax_type_5, // groups
      pax_type_6, // party
      capacity_per_type: `${pax_type_1} / ${pax_type_2} / ${pax_type_3} / ${pax_type_4} / ${pax_type_5} / ${pax_type_6}`,
    };

    const keys = Object.keys(propertiesByTab);
    const specs = keys.map((key: string) => {
      const expand = !!!currSpacesSortKeys.some((el: any) => el === key);
      const defaults = propertiesByTab[key];
      const value =
        mergedObj[key] ||
        (mergedObj[key] === false ? false : defaults.value || '');

      const metricUnitKey = metricUnitKeys[unitsType][defaults.unit as Unit];
      const { maxLength, exp } = defaults;
      const placeholder =
        maxLength && !!exp
          ? t(`common:${exp}`, { maxLength })
          : typeof exp === 'number'
          ? `${t(`common:e.g.`)} ${exp}`
          : !!exp
          ? t(`common:${exp}`)
          : '';
      const fieldObj = {
        ...defaults,
        id: key,
        label: t(`venue:spaces:${key}`),
        unit: t(`common:${metricUnitKey || defaults.unit}`),
        convertTo: !!metricUnitKey && imperial ? defaults.unit : '',
        placeholder,
        expand,
        value: !planFieldsByTab[key]
          ? defaults.value
          : imperial && !!metricUnitKey
          ? convertValueToImperial(value, defaults.unit)
          : value,
      };
      return fieldObj;
    });

    return {
      id,
      type,
      name,
      specs,
      images,
      lexorank,
    };
  };

  // Spaces data by tab
  const currSpaces = allFields.spaces
    ? (data || [])
        .filter((el: any) => el.type === tab.toUpperCase())
        .sort(byProperty(spacesSort.value, spacesSort.order))
        .map((space: any) => getSpaceData(space))
    : [];
  const [localCurrSpaces, setLocalCurrSpaces] = useState(currSpaces);
  const venueSpaces = localCurrSpaces.length ? localCurrSpaces : currSpaces;

  const newSpaceData = getSpaceData({});

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBrowser(true);
    }
  }, []);

  const onDragEnd = (result: any) => {
    if (venueSpaceEditHandler === undefined) {
      return;
    }

    const destinationIndex = result.destination?.index;
    const sourceIndex = result.source.index;

    if (destinationIndex === sourceIndex || destinationIndex === undefined) {
      return;
    }

    const newSpaces = [...currSpaces];
    const [moved] = newSpaces.splice(sourceIndex, 1);
    newSpaces.splice(destinationIndex, 0, moved);

    const prevSpace = newSpaces[destinationIndex - 1];
    const prevLexorank = prevSpace?.lexorank
      ? LexoRank.parse(prevSpace.lexorank)
      : null;
    const nextSpace = newSpaces[destinationIndex + 1];
    const nextLexorank = nextSpace?.lexorank
      ? LexoRank.parse(nextSpace.lexorank)
      : null;

    let newRank = '0|0i0000:';

    if (prevLexorank && nextLexorank) {
      newRank = prevLexorank.between(nextLexorank).toString();
    } else if (prevLexorank) {
      newRank = prevLexorank.genNext().toString();
    } else if (nextLexorank) {
      newRank = nextLexorank.genPrev().toString();
    }
    moved.lexorank = newRank;

    setLocalCurrSpaces(newSpaces);

    const movedSpace = {
      id: currSpaces[sourceIndex].id,
      lexorank: newRank,
    };

    venueSpaceEditHandler(movedSpace);
  };

  return (
    <Row>
      <Col xs={8}>
        <TabBox
          mobileSlide
          title={t('venue:spaces:headline')}
          onChange={(id) => {
            setTab(id);
          }}
          tabs={currSpacesTabs}
        >
          {!!access && (
            <AddItem
              // restricted={true}
              label={`${t(`common:add`)} ${t(
                `venue:spaces:${tab}`
              ).toLowerCase()}`}
              href="/promote-your-business"
              onClick={() =>
                setLayer({
                  bg: true,
                  large: true,
                  children: (
                    <VenueSpaceLayer
                      venueID={venueID}
                      data={newSpaceData}
                      access={access}
                      apply={apply}
                      setLayer={setLayer}
                      setFullLayer={setFullLayer}
                    />
                  ),
                })
              }
            />
          )}

          {!!venueSpaces.length && planDisplayObj[tab][0] ? (
            <>
              {!access && (
                <Sorting
                  className={styles.propsSorting}
                  onChange={(obj: any) => setSpacesSort(obj)}
                  data={currSpacesSort}
                />
              )}

              <DragDropContext onDragEnd={onDragEnd}>
                {isBrowser ? (
                  <Droppable droppableId="venuespaces" direction="vertical">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        <List maxItems={4}>
                          {venueSpaces.map((el: any, i: number) => {
                            return (
                              <Draggable
                                draggableId={`drag-venuespace-${el.id}`}
                                index={i}
                                key={el.id}
                                isDragDisabled={
                                  !access || venueSpaces.length < 2
                                }
                              >
                                {(provided: any) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <VenueSpaceListed
                                      data={el}
                                      access={access}
                                      onClick={() =>
                                        setLayer({
                                          bg: true,
                                          large: true,
                                          children: (
                                            <VenueSpaceLayer
                                              venueID={venueID}
                                              access={access}
                                              apply={apply}
                                              data={el}
                                              setLayer={setLayer}
                                              setFullLayer={setFullLayer}
                                            />
                                          ),
                                        })
                                      }
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </List>
                      </div>
                    )}
                  </Droppable>
                ) : null}
              </DragDropContext>
            </>
          ) : (
            <Empty />
          )}
          <br />
          <br />
          <br />
          <br />
        </TabBox>
      </Col>
    </Row>
  );
};

export default VenueSpaces;
